import React, { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import DynamicCamera from './DynamicCamera';

const CameraController = ({ audioData, vibeConfig, cameraMode }) => {
  const orbitRef = useRef();

  // Cinematic camera positions for different modes
  const cinematicModes = {
    closeup: { distance: 8, height: 2 },
    wide: { distance: 20, height: 5 },
    overhead: { distance: 15, height: 15 },
    side: { distance: 18, height: 0 }
  };

  return (
    <>
      {/* Dynamic third-person camera */}
      {cameraMode === 'dynamic' && (
        <DynamicCamera 
          audioData={audioData} 
          vibeConfig={vibeConfig} 
          enabled={true}
        />
      )}

      {/* Traditional orbit controls - more stable and realistic with beat response */}
      {cameraMode === 'orbit' && (
        <>
          <OrbitControls 
            ref={orbitRef}
            enablePan={false}
            enableZoom={true}
            enableRotate={true}
            autoRotate={vibeConfig?.effects?.rotation}
            autoRotateSpeed={audioData ? 0.1 + audioData.average * 0.3 + (audioData.peak > 0.9 ? audioData.peak * 1.5 : 0) : 0.1} // Less sensitive beat response
            minDistance={8}
            maxDistance={25}
            dampingFactor={0.1} // More damping for smoother movement
            enableDamping={true}
            maxPolarAngle={Math.PI * 0.8} // Prevent going too low
            minPolarAngle={Math.PI * 0.2} // Prevent going too high
          />
          <OrbitPhysics audioData={audioData} orbitRef={orbitRef} />
        </>
      )}

      {/* Cinematic mode with preset positions */}
      {cameraMode === 'cinematic' && (
        <CinematicCamera 
          audioData={audioData} 
          vibeConfig={vibeConfig}
          modes={cinematicModes}
        />
      )}
    </>
  );
};

// Cinematic camera component with preset dramatic angles and physics
const CinematicCamera = ({ audioData, vibeConfig, modes }) => {
  const { camera } = useThree();
  const currentModeRef = useRef('wide');
  const transitionTimeRef = useRef(0);
  const modeChangeIntervalRef = useRef(8); // Change mode every 8 seconds
  
  // Physics for cinematic camera
  const cinematicVelocityRef = useRef(new THREE.Vector3(0, 0, 0));
  const cinematicJoltRef = useRef(new THREE.Vector3(0, 0, 0));
  const lastCinematicBeatRef = useRef(0);

  useFrame((state) => {
    if (!audioData) return;

    const time = state.clock.getElapsedTime();
    const deltaTime = state.clock.getDelta();
    
    // Beat detection for cinematic jolts - much less sensitive
    const isBeat = audioData.peak > 0.9; // Higher threshold
    if (isBeat && time - lastCinematicBeatRef.current > 1.5) { // Longer cooldown
      lastCinematicBeatRef.current = time;
      
      // Add cinematic jolt - reduced strength
      const joltStrength = audioData.peak * 1; // Reduced from 1.5
      cinematicJoltRef.current.set(
        (Math.random() - 0.5) * joltStrength,
        (Math.random() - 0.5) * joltStrength * 0.2, // Reduced from 0.3
        (Math.random() - 0.5) * joltStrength * 0.3  // Reduced from 0.5
      );
    }
    
    // Change cinematic mode based on time and audio intensity
    if (time - transitionTimeRef.current > modeChangeIntervalRef.current) {
      const modeKeys = Object.keys(modes);
      const currentIndex = modeKeys.indexOf(currentModeRef.current);
      const nextIndex = (currentIndex + 1) % modeKeys.length;
      currentModeRef.current = modeKeys[nextIndex];
      transitionTimeRef.current = time;
      
      // Shorter intervals during intense audio
      modeChangeIntervalRef.current = audioData.peak > 0.7 ? 4 : 8;
    }

    const currentMode = modes[currentModeRef.current];
    const { distance, height } = currentMode;
    
    // Smooth cinematic movement with physics
    const angle = time * 0.2 + audioData.bass * 2;
    const audioDistance = distance + audioData.peak * 5;
    const audioHeight = height + audioData.mid * 3;
    
    const baseX = Math.cos(angle) * audioDistance;
    const baseZ = Math.sin(angle) * audioDistance;
    const baseY = audioHeight + Math.sin(time * 0.5) * 2;
    
    // Apply physics-based jolt
    cinematicVelocityRef.current.add(cinematicJoltRef.current.clone().multiplyScalar(deltaTime));
    cinematicVelocityRef.current.multiplyScalar(0.88); // Damping
    cinematicJoltRef.current.multiplyScalar(0.9); // Jolt decay
    
    const targetX = baseX + cinematicVelocityRef.current.x;
    const targetY = baseY + cinematicVelocityRef.current.y;
    const targetZ = baseZ + cinematicVelocityRef.current.z;
    
    // Smooth camera transition with physics
    camera.position.lerp(
      new THREE.Vector3(targetX, targetY, targetZ), 
      0.02
    );
    
    // Look at center with slight audio-reactive offset and physics jolt
    const lookAtTarget = new THREE.Vector3(
      Math.sin(time * 0.3) * audioData.treble + cinematicVelocityRef.current.x * 0.1,
      Math.cos(time * 0.4) * audioData.bass + cinematicVelocityRef.current.y * 0.1,
      cinematicVelocityRef.current.z * 0.05
    );
    
    camera.lookAt(lookAtTarget);
    
    // Dramatic FOV changes for cinematic effect with beat jolts
    const baseFOV = currentModeRef.current === 'closeup' ? 60 : 
                   currentModeRef.current === 'wide' ? 85 : 75;
    const fovVariation = audioData.peak * 20;
    const joltFOV = Math.abs(cinematicJoltRef.current.length()) * 5;
    camera.fov = baseFOV + fovVariation + joltFOV;
    camera.updateProjectionMatrix();
  });

  return null;
};

// Physics component for orbit controls
const OrbitPhysics = ({ audioData, orbitRef }) => {
  const { camera } = useThree();
  const lastBeatRef = useRef(0);
  const joltVelocityRef = useRef(new THREE.Vector3(0, 0, 0));
  const originalPositionRef = useRef(new THREE.Vector3());

  useFrame((state) => {
    if (!audioData || !orbitRef.current) return;

    const time = state.clock.getElapsedTime();
    
    // Beat detection for orbit jolts - much less sensitive
    const isBeat = audioData.peak > 0.9; // Higher threshold
    if (isBeat && time - lastBeatRef.current > 1.8) { // Longer cooldown
      lastBeatRef.current = time;
      
      // Store original position
      originalPositionRef.current.copy(camera.position);
      
      // Add physics-based jolt - reduced strength
      const joltStrength = audioData.peak * 0.5; // Reduced from 0.8
      joltVelocityRef.current.set(
        (Math.random() - 0.5) * joltStrength,
        (Math.random() - 0.5) * joltStrength * 0.3, // Reduced from 0.5
        (Math.random() - 0.5) * joltStrength * 0.2  // Reduced from 0.3
      );
    }
    
    // Apply jolt physics
    if (joltVelocityRef.current.length() > 0.01) {
      // Apply jolt to camera position
      const joltPosition = originalPositionRef.current.clone().add(joltVelocityRef.current);
      camera.position.lerp(joltPosition, 0.3);
      
      // Decay jolt velocity
      joltVelocityRef.current.multiplyScalar(0.85);
      
      // Update orbit controls target
      orbitRef.current.update();
    }
  });

  return null;
};

export default CameraController;